// Demo Conversion Funnel SQL constants. Both queries are kept as static
// strings (no parameterisation) to match the existing pages/FunnelAnalytics
// pattern; cohort filtering is done client-side via applyFilters.
//
// Spine backfilled to Jan 2025. Caveats:
//  - reporting.demo organic from 2026-01-26 05:09 UTC; bulk historical append
//    covers everything up to 2026-01-25 23:59 UTC (demo metadata reconstructed
//    from transcripts, expect sparser fields).
//  - reporting.lead_cal organic from 2026-02-26 21:36 UTC; bulk historical
//    append up to 2026-02-26 21:30 UTC. demo_attended is true for pre-25-Jan
//    bookings but false for 25-Jan-2026 → 26-Feb-2026 (pending re-backfill).

import { logrocketLink, mixpanelLink } from "../../lib/funnel/helpers";

// One row per (demo, attendee). Multi-attendee demos produce one row per
// attendee. Spine is reporting.demo with is_demo=true; joins to public.users
// give signup/role/country/billing, and a booking_match CTE pairs demos with
// reporting.lead_cal bookings (lower-cased email ∈ attendee_emails, ±60 days,
// closest in time).
export const DEMO_FUNNEL_SQL = `
WITH cohort AS (
    SELECT '2025-01-01'::timestamptz AS start_ts,
           '2099-01-01'::timestamptz AS end_ts
),

demos AS (
    -- Dedup on provider_meeting_id: the same meeting can land in
    -- reporting.demo twice (e.g. recall.ai + transcript captured separately).
    -- Mar 2026 has 2 such pairs; without this the headline overcounts the
    -- spine. Keep the earliest external_id for stability.
    SELECT DISTINCT ON (COALESCE(d.provider_meeting_id, d.external_id::text))
        d.external_id            AS demo_external_id,
        d.created_date_utc       AS demo_date_utc,
        d.organiser_email,
        d.attendee_emails,
        d.company_name           AS company_name_native,
        d.follow_up_context,
        (d.follow_up_context ->> 'lead_status')                          AS lead_status,
        (d.follow_up_context ->> 'signed_up_on_call')::boolean           AS signed_up_on_call,
        (d.follow_up_context ->> 'account_creation_mentioned')::boolean  AS account_creation_mentioned,
        (d.follow_up_context ->> 'calendar_connected')::boolean          AS calendar_connected_at_demo,
        (d.follow_up_context ->> 'timeline_urgency')                     AS timeline_urgency,
        (d.follow_up_context ->> 'demo_type')                            AS demo_type,
        (d.follow_up_context ->> 'priority')                             AS priority,
        (d.follow_up_context ->> 'company_name')                         AS company_name_from_context,
        (d.follow_up_context ->> 'adviser_count')::int                   AS adviser_count_from_demo,
        (d.follow_up_context -> 'competitors_mentioned')                 AS competitors_mentioned,
        (d.follow_up_context -> 'integration_requests')                  AS integration_requests,
        (d.follow_up_context -> 'security_concerns')                     AS security_concerns,
        (d.follow_up_context -> 'pain_points')                           AS pain_points,
        (d.follow_up_context -> 'pricing_signals')                       AS pricing_signals,
        (d.follow_up_context -> 'value_of_marloo')                       AS value_of_marloo,
        (d.follow_up_context -> 'objections')                            AS objections,
        (d.follow_up_context -> 'transcript_signals')                    AS transcript_signals
        -- verbal attribution: pending follow_up_context.attribution_mention backfill
    FROM reporting.demo d, cohort c
    WHERE (d.follow_up_context ->> 'is_demo') = 'true'
      AND d.organiser_email ILIKE '%@gomarloo.com'
      AND d.created_date_utc >= c.start_ts
      AND d.created_date_utc <  c.end_ts
    ORDER BY COALESCE(d.provider_meeting_id, d.external_id::text), d.external_id
),

demo_attendees AS (
    SELECT
        dm.demo_external_id,
        dm.demo_date_utc,
        lower(trim(att)) AS attendee_email
    FROM demos dm
    CROSS JOIN LATERAL unnest(dm.attendee_emails) AS att
    WHERE att IS NOT NULL
      AND att <> ''
      AND att ILIKE '%@%'
      AND att NOT ILIKE '%@gomarloo.com'
),

first_demo_per_attendee AS (
    SELECT
        attendee_email,
        MIN(demo_date_utc) AS first_demo_date
    FROM demo_attendees
    GROUP BY attendee_email
),

booking_match AS (
    SELECT DISTINCT ON (dm.demo_external_id)
        dm.demo_external_id,
        lc.external_id              AS lead_cal_external_id,
        lc.client_email             AS booker_email,
        lc.created_date_utc         AS booked_at,
        lc.meeting_type,
        lc.country_code             AS booking_country_code,
        lc.advisor_count            AS booking_advisor_count,
        lc.utm_source,
        lc.utm_medium,
        lc.utm_campaign,
        lc.utm_content,
        lc.utm_term,
        CASE
            WHEN lc.utm_source ILIKE ANY (ARRAY['facebook','fb','ig','instagram','meta']) THEN 'Meta'
            WHEN lc.utm_source = 'google'                                                 THEN 'Google paid search'
            WHEN lc.utm_source = 'linkedin'                                               THEN 'LinkedIn'
            WHEN lc.utm_source = 'twitter'                                                THEN 'Twitter/X'
            WHEN lc.utm_source ILIKE '%email%' OR lc.utm_campaign ILIKE '%email%'         THEN 'Email signature'
            WHEN lc.utm_campaign = 'app-demo' AND lc.utm_source IS NULL                   THEN 'In-app demo CTA'
            WHEN lc.utm_source IS NULL AND lc.utm_campaign IS NULL                        THEN 'Untagged'
            ELSE 'Other'
        END AS booking_channel_bucket
    FROM demos dm
    JOIN reporting.lead_cal lc
      ON lower(lc.client_email) = ANY (
            ARRAY(SELECT lower(trim(x)) FROM unnest(dm.attendee_emails) AS x)
         )
     AND lc.created_date_utc BETWEEN dm.demo_date_utc - interval '60 days'
                                 AND dm.demo_date_utc + interval '60 days'
    ORDER BY
        dm.demo_external_id,
        abs(extract(epoch FROM (lc.created_date_utc - dm.demo_date_utc)))
),

attendee_user AS (
    SELECT DISTINCT ON (lower(u.email))
        lower(u.email)               AS attendee_email,
        u.external_id                AS user_external_id,
        TRIM(COALESCE(u.first_name,'') || ' ' || COALESCE(u.last_name,'')) AS user_full_name,
        u.created_at_utc             AS signup_date_utc,
        u.attribution                AS user_attribution_raw,
        u.attribution_category::text AS user_attribution_category,
        ur.role::text                AS user_role,
        ur.company_external_id,
        cmp.name                     AS company_name_from_user,
        cmp.country_code             AS user_country_code
    FROM public.users u
    LEFT JOIN public.user_roles ur  ON ur.user_external_id = u.external_id
    LEFT JOIN public.companies  cmp ON cmp.external_id = ur.company_external_id
    WHERE u.email IS NOT NULL
      AND u.email NOT ILIKE '%@gomarloo.com'
    ORDER BY
        lower(u.email),
        (ur.status = 'active')      DESC NULLS LAST,
        (ur.role::text = 'admin')   DESC NULLS LAST,
        (ur.role::text = 'adviser') DESC NULLS LAST
),

paid AS (
    SELECT
        cb.company_external_id,
        bool_or(cb.billing_plan::text IN ('plus','pro')) AS on_paid_plan,
        MIN(cb.subscription_started_date_utc) FILTER (
            WHERE cb.billing_plan::text IN ('plus','pro')
        ) AS subscription_started_date_utc,
        (array_agg(cb.billing_plan::text ORDER BY cb.subscription_started_date_utc DESC NULLS LAST))[1] AS billing_plan
    FROM public.company_billing cb
    GROUP BY cb.company_external_id
),

activated AS (
    SELECT
        s.user_external_id,
        MIN(s.created_date_utc) AS first_synopsis_date
    FROM public.meeting_synopsis s
    JOIN public.meeting m ON m.external_id = s.meeting_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
    GROUP BY s.user_external_id
)

SELECT
    dm.demo_external_id,
    dm.demo_date_utc,
    dm.organiser_email,
    dm.company_name_native,
    dm.company_name_from_context,
    dm.lead_status,
    dm.signed_up_on_call,
    dm.account_creation_mentioned,
    dm.calendar_connected_at_demo,
    dm.timeline_urgency,
    dm.demo_type,
    dm.priority,
    dm.adviser_count_from_demo,
    dm.competitors_mentioned,
    dm.integration_requests,
    dm.security_concerns,
    dm.pain_points,
    dm.pricing_signals,
    dm.value_of_marloo,
    dm.objections,
    dm.transcript_signals,

    da.attendee_email,
    fd.first_demo_date,
    (dm.demo_date_utc = fd.first_demo_date) AS is_first_demo_for_attendee,

    au.user_external_id,
    au.user_full_name,
    au.signup_date_utc,
    au.user_role,
    au.user_country_code,
    au.company_external_id,
    au.company_name_from_user,
    au.user_attribution_raw,
    au.user_attribution_category,
    -- Boundaries are zero-aligned to the demo timestamp. Previously 'on_day'
    -- ran from demo - 1d to demo + 1d, which mixed pre-demo signups (negative
    -- days) into the bucket and silently broke the days-to-signup histogram
    -- (which filters days >= 0). Now 'before' is anything before the demo,
    -- 'on_day' is the 24h after.
    CASE
        WHEN au.user_external_id IS NULL                                          THEN 'never'
        WHEN au.signup_date_utc <  dm.demo_date_utc                               THEN 'before'
        WHEN au.signup_date_utc <  dm.demo_date_utc + interval '1 day'            THEN 'on_day'
        WHEN au.signup_date_utc <  dm.demo_date_utc + interval '7 days'           THEN 'within_7d'
        WHEN au.signup_date_utc <  dm.demo_date_utc + interval '30 days'          THEN 'within_30d'
        ELSE 'later'
    END AS signup_timing_bucket,
    EXTRACT(EPOCH FROM (au.signup_date_utc - dm.demo_date_utc)) / 86400.0 AS days_demo_to_signup,

    act.first_synopsis_date,
    (act.first_synopsis_date IS NOT NULL) AS activated,
    p.on_paid_plan,
    p.billing_plan,
    p.subscription_started_date_utc,

    bm.lead_cal_external_id,
    bm.booker_email,
    bm.booked_at,
    bm.meeting_type,
    bm.booking_country_code,
    bm.booking_advisor_count,
    bm.utm_source,
    bm.utm_medium,
    bm.utm_campaign,
    bm.utm_content,
    bm.utm_term,
    bm.booking_channel_bucket,
    (bm.lead_cal_external_id IS NOT NULL) AS has_lead_cal_match

FROM demo_attendees da
JOIN demos                        dm  ON dm.demo_external_id = da.demo_external_id
LEFT JOIN first_demo_per_attendee fd  ON fd.attendee_email = da.attendee_email
LEFT JOIN attendee_user           au  ON au.attendee_email = da.attendee_email
LEFT JOIN activated               act ON act.user_external_id = au.user_external_id
LEFT JOIN paid                    p   ON p.company_external_id = au.company_external_id
LEFT JOIN booking_match           bm  ON bm.demo_external_id = dm.demo_external_id

ORDER BY dm.demo_date_utc DESC, da.attendee_email
`;

// One row per Cal.com booking with demo match status, booker outcome, and
// bucketed channel. Feeds the supplementary lead_cal panel (linkage health,
// bookings by channel, matched-subset booking → demo conversion, no-show
// recovery).
export const LEAD_CAL_SUPPLEMENTARY_SQL = `
WITH cohort AS (
    SELECT '2025-01-01'::timestamptz AS start_ts,
           '2099-01-01'::timestamptz AS end_ts
),

bookings AS (
    SELECT
        lc.external_id              AS lead_cal_external_id,
        lc.client_email,
        lower(lc.client_email)      AS client_email_lc,
        lc.created_date_utc         AS booked_at,
        date_trunc('month', lc.created_date_utc) AS booked_month,
        lc.meeting_type,
        lc.country_code             AS booking_country_code,
        lc.advisor_count            AS booking_advisor_count,
        lc.utm_source,
        lc.utm_medium,
        lc.utm_campaign,
        lc.utm_content,
        lc.utm_term,
        CASE
            WHEN lc.utm_source ILIKE ANY (ARRAY['facebook','fb','ig','instagram','meta']) THEN 'Meta'
            WHEN lc.utm_source = 'google'                                                 THEN 'Google paid search'
            WHEN lc.utm_source = 'linkedin'                                               THEN 'LinkedIn'
            WHEN lc.utm_source = 'twitter'                                                THEN 'Twitter/X'
            WHEN lc.utm_source ILIKE '%email%' OR lc.utm_campaign ILIKE '%email%'         THEN 'Email signature'
            WHEN lc.utm_campaign = 'app-demo' AND lc.utm_source IS NULL                   THEN 'In-app demo CTA'
            WHEN lc.utm_source IS NULL AND lc.utm_campaign IS NULL                        THEN 'Untagged'
            ELSE 'Other'
        END AS channel_bucket,
        (lc.utm_source IS NOT NULL AND lc.utm_source <> '') AS has_utm_source
    FROM reporting.lead_cal lc, cohort c
    WHERE lc.created_date_utc >= c.start_ts
      AND lc.created_date_utc <  c.end_ts
),

demo_window AS (
    SELECT
        d.external_id           AS demo_external_id,
        d.created_date_utc      AS demo_date_utc,
        d.attendee_emails,
        ((d.follow_up_context ->> 'is_demo') = 'true') AS is_demo_true
    FROM reporting.demo d
    WHERE d.organiser_email ILIKE '%@gomarloo.com'
      AND d.created_date_utc >= (SELECT start_ts - interval '60 days' FROM cohort)
      AND d.created_date_utc <  (SELECT end_ts   + interval '60 days' FROM cohort)
),

demo_match AS (
    SELECT DISTINCT ON (b.lead_cal_external_id)
        b.lead_cal_external_id,
        dw.demo_external_id,
        dw.demo_date_utc,
        dw.is_demo_true
    FROM bookings b
    JOIN demo_window dw
      ON b.client_email_lc = ANY (
            ARRAY(SELECT lower(trim(x)) FROM unnest(dw.attendee_emails) AS x)
         )
     AND dw.demo_date_utc BETWEEN b.booked_at - interval '60 days'
                              AND b.booked_at + interval '60 days'
    ORDER BY
        b.lead_cal_external_id,
        abs(extract(epoch FROM (dw.demo_date_utc - b.booked_at)))
),

booker_user AS (
    SELECT DISTINCT ON (lower(u.email))
        lower(u.email)        AS user_email,
        u.external_id         AS user_external_id,
        u.created_at_utc      AS signup_date_utc,
        ur.company_external_id
    FROM public.users u
    LEFT JOIN public.user_roles ur ON ur.user_external_id = u.external_id
    WHERE u.email NOT ILIKE '%@gomarloo.com'
    ORDER BY
        lower(u.email),
        (ur.status = 'active') DESC NULLS LAST
),

paid AS (
    SELECT
        cb.company_external_id,
        bool_or(cb.billing_plan::text IN ('plus','pro')) AS on_paid_plan,
        (array_agg(cb.billing_plan::text ORDER BY cb.subscription_started_date_utc DESC NULLS LAST))[1] AS billing_plan
    FROM public.company_billing cb
    GROUP BY cb.company_external_id
)

SELECT
    b.lead_cal_external_id,
    b.client_email,
    b.booked_at,
    b.booked_month,
    b.meeting_type,
    b.booking_country_code,
    b.booking_advisor_count,
    b.utm_source,
    b.utm_medium,
    b.utm_campaign,
    b.utm_content,
    b.utm_term,
    b.channel_bucket,
    b.has_utm_source,
    (dm.demo_external_id IS NOT NULL AND dm.is_demo_true)            AS matched_real_demo,
    (dm.demo_external_id IS NOT NULL AND dm.is_demo_true = false)    AS matched_no_show_only,
    (dm.demo_external_id IS NULL)                                     AS unmatched,
    dm.demo_external_id      AS matched_demo_external_id,
    dm.demo_date_utc         AS matched_demo_date_utc,
    bu.user_external_id      AS booker_user_external_id,
    bu.signup_date_utc       AS booker_signup_date_utc,
    (bu.user_external_id IS NOT NULL) AS booker_signed_up,
    p.on_paid_plan           AS booker_company_on_paid_plan,
    p.billing_plan           AS booker_billing_plan
FROM bookings b
LEFT JOIN demo_match  dm ON dm.lead_cal_external_id = b.lead_cal_external_id
LEFT JOIN booker_user bu ON bu.user_email = b.client_email_lc
LEFT JOIN paid         p ON p.company_external_id = bu.company_external_id
ORDER BY b.booked_at DESC
`;

// ── Row types + mappers ─────────────────────────────────────────────────────

export type SignupTimingBucket = "before" | "on_day" | "within_7d" | "within_30d" | "later" | "never";

export type DemoAttendeeRow = {
  // Demo identity
  demo_external_id: string;
  demo_date_utc: string;
  organiser_email: string | null;
  company_name_native: string | null;
  company_name_from_context: string | null;
  // Demo metadata
  lead_status: string | null;
  signed_up_on_call: boolean | null;
  account_creation_mentioned: boolean | null;
  calendar_connected_at_demo: boolean | null;
  timeline_urgency: string | null;
  demo_type: string | null;
  priority: string | null;
  adviser_count_from_demo: number | null;
  competitors_mentioned: unknown;
  integration_requests: unknown;
  security_concerns: unknown;
  pain_points: unknown;
  pricing_signals: unknown;
  value_of_marloo: unknown;
  objections: unknown;
  transcript_signals: unknown;
  // Attendee
  attendee_email: string;
  first_demo_date: string | null;
  is_first_demo_for_attendee: boolean;
  // Signup outcome
  user_external_id: string | null;
  user_full_name: string | null;
  signup_date_utc: string | null;
  user_role: string | null;
  user_country_code: string | null;
  company_external_id: string | null;
  company_name_from_user: string | null;
  user_attribution_raw: string | null;
  user_attribution_category: string | null;
  signup_timing_bucket: SignupTimingBucket;
  days_demo_to_signup: number | null;
  // Activation + paid
  first_synopsis_date: string | null;
  activated: boolean;
  on_paid_plan: boolean | null;
  billing_plan: string | null;
  subscription_started_date_utc: string | null;
  // lead_cal enrichment
  lead_cal_external_id: string | null;
  booker_email: string | null;
  booked_at: string | null;
  meeting_type: string | null;
  booking_country_code: string | null;
  booking_advisor_count: number | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  booking_channel_bucket: string | null;
  has_lead_cal_match: boolean;
  // Derived (UI-only)
  logrocket_link: string | null;
  mixpanel_link: string | null;
};

export type LeadCalRow = {
  lead_cal_external_id: string;
  client_email: string | null;
  booked_at: string;
  booked_month: string | null;
  meeting_type: string | null;
  booking_country_code: string | null;
  booking_advisor_count: number | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  channel_bucket: string;
  has_utm_source: boolean;
  matched_real_demo: boolean;
  matched_no_show_only: boolean;
  unmatched: boolean;
  matched_demo_external_id: string | null;
  matched_demo_date_utc: string | null;
  booker_user_external_id: string | null;
  booker_signup_date_utc: string | null;
  booker_signed_up: boolean;
  booker_company_on_paid_plan: boolean | null;
  booker_billing_plan: string | null;
};

function indexer(columns: string[]): Record<string, number> {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  return idx;
}

function str(v: unknown): string { return v == null ? "" : String(v); }
function strOrNull(v: unknown): string | null { return v == null ? null : String(v); }
function num(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function bool(v: unknown): boolean { return v === true || v === "t" || v === "true"; }
function boolOrNull(v: unknown): boolean | null {
  if (v == null) return null;
  return bool(v);
}

function isSignupTimingBucket(v: string): v is SignupTimingBucket {
  return v === "before" || v === "on_day" || v === "within_7d" || v === "within_30d" || v === "later" || v === "never";
}

export function mapDemoAttendeeRows(columns: string[], rows: unknown[][]): DemoAttendeeRow[] {
  const idx = indexer(columns);
  const get = (row: unknown[], key: string): unknown => idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((row) => {
    const userId = strOrNull(get(row, "user_external_id"));
    const bucketRaw = str(get(row, "signup_timing_bucket")) || "never";
    const bucket: SignupTimingBucket = isSignupTimingBucket(bucketRaw) ? bucketRaw : "never";
    return {
      demo_external_id: str(get(row, "demo_external_id")),
      demo_date_utc: str(get(row, "demo_date_utc")),
      organiser_email: strOrNull(get(row, "organiser_email")),
      company_name_native: strOrNull(get(row, "company_name_native")),
      company_name_from_context: strOrNull(get(row, "company_name_from_context")),
      lead_status: strOrNull(get(row, "lead_status")),
      signed_up_on_call: boolOrNull(get(row, "signed_up_on_call")),
      account_creation_mentioned: boolOrNull(get(row, "account_creation_mentioned")),
      calendar_connected_at_demo: boolOrNull(get(row, "calendar_connected_at_demo")),
      timeline_urgency: strOrNull(get(row, "timeline_urgency")),
      demo_type: strOrNull(get(row, "demo_type")),
      priority: strOrNull(get(row, "priority")),
      adviser_count_from_demo: numOrNull(get(row, "adviser_count_from_demo")),
      competitors_mentioned: get(row, "competitors_mentioned"),
      integration_requests: get(row, "integration_requests"),
      security_concerns: get(row, "security_concerns"),
      pain_points: get(row, "pain_points"),
      pricing_signals: get(row, "pricing_signals"),
      value_of_marloo: get(row, "value_of_marloo"),
      objections: get(row, "objections"),
      transcript_signals: get(row, "transcript_signals"),
      attendee_email: str(get(row, "attendee_email")),
      first_demo_date: strOrNull(get(row, "first_demo_date")),
      is_first_demo_for_attendee: bool(get(row, "is_first_demo_for_attendee")),
      user_external_id: userId,
      user_full_name: strOrNull(get(row, "user_full_name")),
      signup_date_utc: strOrNull(get(row, "signup_date_utc")),
      user_role: strOrNull(get(row, "user_role")),
      user_country_code: strOrNull(get(row, "user_country_code")),
      company_external_id: strOrNull(get(row, "company_external_id")),
      company_name_from_user: strOrNull(get(row, "company_name_from_user")),
      user_attribution_raw: strOrNull(get(row, "user_attribution_raw")),
      user_attribution_category: strOrNull(get(row, "user_attribution_category")),
      signup_timing_bucket: bucket,
      days_demo_to_signup: numOrNull(get(row, "days_demo_to_signup")),
      first_synopsis_date: strOrNull(get(row, "first_synopsis_date")),
      activated: bool(get(row, "activated")),
      on_paid_plan: boolOrNull(get(row, "on_paid_plan")),
      billing_plan: strOrNull(get(row, "billing_plan")),
      subscription_started_date_utc: strOrNull(get(row, "subscription_started_date_utc")),
      lead_cal_external_id: strOrNull(get(row, "lead_cal_external_id")),
      booker_email: strOrNull(get(row, "booker_email")),
      booked_at: strOrNull(get(row, "booked_at")),
      meeting_type: strOrNull(get(row, "meeting_type")),
      booking_country_code: strOrNull(get(row, "booking_country_code")),
      booking_advisor_count: numOrNull(get(row, "booking_advisor_count")),
      utm_source: strOrNull(get(row, "utm_source")),
      utm_medium: strOrNull(get(row, "utm_medium")),
      utm_campaign: strOrNull(get(row, "utm_campaign")),
      utm_content: strOrNull(get(row, "utm_content")),
      utm_term: strOrNull(get(row, "utm_term")),
      booking_channel_bucket: strOrNull(get(row, "booking_channel_bucket")),
      has_lead_cal_match: bool(get(row, "has_lead_cal_match")),
      logrocket_link: userId ? logrocketLink(userId) : null,
      mixpanel_link: userId ? mixpanelLink(userId) : null,
    };
  });
}

export function mapLeadCalRows(columns: string[], rows: unknown[][]): LeadCalRow[] {
  const idx = indexer(columns);
  const get = (row: unknown[], key: string): unknown => idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((row) => ({
    lead_cal_external_id: str(get(row, "lead_cal_external_id")),
    client_email: strOrNull(get(row, "client_email")),
    booked_at: str(get(row, "booked_at")),
    booked_month: strOrNull(get(row, "booked_month")),
    meeting_type: strOrNull(get(row, "meeting_type")),
    booking_country_code: strOrNull(get(row, "booking_country_code")),
    booking_advisor_count: numOrNull(get(row, "booking_advisor_count")),
    utm_source: strOrNull(get(row, "utm_source")),
    utm_medium: strOrNull(get(row, "utm_medium")),
    utm_campaign: strOrNull(get(row, "utm_campaign")),
    utm_content: strOrNull(get(row, "utm_content")),
    utm_term: strOrNull(get(row, "utm_term")),
    channel_bucket: str(get(row, "channel_bucket")),
    has_utm_source: bool(get(row, "has_utm_source")),
    matched_real_demo: bool(get(row, "matched_real_demo")),
    matched_no_show_only: bool(get(row, "matched_no_show_only")),
    unmatched: bool(get(row, "unmatched")),
    matched_demo_external_id: strOrNull(get(row, "matched_demo_external_id")),
    matched_demo_date_utc: strOrNull(get(row, "matched_demo_date_utc")),
    booker_user_external_id: strOrNull(get(row, "booker_user_external_id")),
    booker_signup_date_utc: strOrNull(get(row, "booker_signup_date_utc")),
    booker_signed_up: bool(get(row, "booker_signed_up")),
    booker_company_on_paid_plan: boolOrNull(get(row, "booker_company_on_paid_plan")),
    booker_billing_plan: strOrNull(get(row, "booker_billing_plan")),
  }));
}
